import { useMemo } from 'react';

import { InputSize, PillSize } from '@/shared/types/types';
interface UseInputStylesProps {
  size?: InputSize;
  error?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Por defecto `true`: con `fullWidth` falso aplica `ui-min-w-72` al control. */
  minWidthEnabled?: boolean;
  caret?: boolean;
  errorCursor?: boolean;
  paddingClasses: string;
  autosize?: boolean;
  pill: PillSize;
}

export const useInputStyles = ({
  size = 'sm',
  error = '',
  disabled = false,
  fullWidth = false,
  minWidthEnabled = true,
  caret = true,
  errorCursor = false,
  paddingClasses,
  autosize = false,
  pill = 'none',
}: UseInputStylesProps) => {
  /** Solo `py` + tipografía + altura: `pl`/`pr` viven en `paddingClasses` para no pisar slots. */
  const inputSizes = useMemo(
    () => ({
      xs: 'ui-h-control-xs ui-text-body-sm-regular ui-font-body-sm-regular ui-leading-body-sm-regular ui-tracking-body-sm ui-py-4',
      sm: 'ui-h-control-sm ui-text-body-md-regular ui-font-body-md-regular ui-leading-body-md-regular ui-tracking-body-md ui-py-4',
      md: 'ui-h-control-md ui-text-body-md-regular ui-font-body-md-regular ui-leading-body-md-regular ui-tracking-body-md ui-py-8',
      lg: 'ui-h-control-lg ui-text-body-lg-regular ui-font-body-lg-regular ui-leading-body-lg-regular ui-tracking-body-lg ui-py-8',
    }),
    []
  );
  const pillClasses = useMemo(
    () => ({
      none: '',
      xs: 'ui-rounded-control-xs',
      sm: 'ui-rounded-control-sm',
      md: 'ui-rounded-control-md',
      lg: 'ui-rounded-control-lg',
      pill: 'ui-rounded-control-pill',
    }),
    []
  );

  const baseClasses = useMemo(() => {
    const hasErrorState = error || errorCursor;

    const widthClasses = fullWidth || !minWidthEnabled ? 'ui-w-full' : 'ui-min-w-72 ui-w-full';

    const classes = [
      'ui-box-border',
      widthClasses,
      'ui-border-1 ui-!font-normal',
      hasErrorState ? '!ui-border-danger' : 'ui-border-default',
      hasErrorState
        ? 'focus-visible:!ui-outline-danger focus-visible:!ui-outline-2'
        : 'focus-visible:ui-outline-brand',
      'ui-transition-all ui-duration-100 ui-ease-in-out',
      'disabled:ui-text-disabled disabled:ui-bg-surface-disabled disabled:ui-border-disabled',
      'disabled:hover:ui-border-disabled disabled:active:ui-border-disabled',
      'disabled:focus:ui-outline-none disabled:focus-visible:!ui-outline-none',
      pillClasses[pill],
      !caret
        ? 'ui-caret-transparent'
        : errorCursor
          ? '[caret-color:var(--ui-color-danger-default)]'
          : '',
      inputSizes[size],
      /** Después de tamaños: `ui-pl-*` / `ui-pr-*` (slots + carril) no deben quedar bajo `ui-p-*` / `ui-px-*`. */
      paddingClasses,
      autosize ? 'field-sizing-content' : '',
      hasErrorState ? 'ui-bg-surface-danger' : 'ui-bg-fill',
    ];

    // Clases condicionales para error
    if (hasErrorState) {
      classes.push('placeholder:ui-text-danger');
    } else {
      classes.push('placeholder:ui-text-tertiary');
    }

    return classes.filter(Boolean).join(' ');
  }, [
    fullWidth,
    minWidthEnabled,
    paddingClasses,
    caret,
    errorCursor,
    inputSizes,
    size,
    error,
    autosize,
    pill,
    pillClasses,
  ]);

  const containerClasses = useMemo(() => {
    const classes = ['ui-relative ui-w-full'];
    if (!disabled) {
      classes.push('ui-group');
    }

    if (disabled) {
      classes.push('ui-text-primary');
    } else if (error || errorCursor) {
      classes.push('ui-text-danger');
    }

    return classes.join(' ');
  }, [disabled, error, errorCursor]);

  const labelClasses = useMemo(() => {
    const base =
      'ui-text-label-md-regular ui-font-label-md-semibold ui-leading-label-md-semibold ui-tracking-label-md';
    if (disabled) {
      return `${base} ui-text-disabled`;
    }
    return error ? `${base} ui-text-danger` : `${base} ui-text-primary`;
  }, [error, disabled]);

  return {
    inputSizes,
    baseClasses,
    containerClasses,
    labelClasses,
  };
};
